import {
	AfterViewInit,
	Component,
	Input,
	OnChanges,
	OnDestroy,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Subscription } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	AlarmStatus,
	alarmOverrideText,
	armedAlarmText,
	disarmedAlarmText,
} from 'src/app/models/data/ajax';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { EventHistory, UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { CalendarDateService } from 'src/app/services/calendar-date.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { DatePipe } from '@angular/common';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import { DataSetTableComponent } from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
	EventTypeFormatPipe,
	EndVisitTypeFormatPipe,
	EmptyDataPipe,
	ShortTimezonePipe,
} from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-device-event-history',
	templateUrl: './device-event-history.component.html',
	styleUrls: [
		'./device-event-history.component.scss',
		'../../../common/main.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		EventTypeFormatPipe,
		EndVisitTypeFormatPipe,
		EmptyDataPipe,
		ShortTimezonePipe,
		DataSetLoadingRowComponent,
	],
})
export class DeviceEventHistoryComponent
	implements AfterViewInit, OnChanges, OnDestroy
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	AlarmStatus = AlarmStatus;
	displayedColumns: string[] = [];
	dataSource = new MatTableDataSource<EventHistory>();

	@Input() deviceId = '';
	@Input() device?: any;
	@Input() userProfile?: any;

	private filterSubscription?: Subscription;
	canGenerateEventHistoryReport!: boolean;
	isDownloadReportDisabled = false;
	filters: QueryParam[] = [];
	companyTypeFlag?: boolean;

	filterLabels = {
		defaultPlaceholder: $localize`:@@deviceEventHistory-defaultPlaceholder:Search working for...`,
		userName: $localize`:@@deviceEventHistory-fieldLabelUserName:User Name`,
		workingFor: $localize`:@@deviceEventHistory-fieldLabelWorkingFor:Working For`,
		propertyName: $localize`:@@deviceEventHistory-fieldLabelPropertyName:Property Name`,
		deviceLocation: $localize`:@@deviceEventHistory-fieldLabelDeviceLocation:Device Location`,
		eventType: $localize`:@@deviceEventHistory-fieldLabelEventType:Event Type`,
		eventStart: $localize`:@@deviceEventHistory-fieldLabelEventStart:Event Start`,
		eventEnd: $localize`:@@deviceEventHistory-fieldLabelEventEnd:Event End`,
		endVisitType: $localize`:@@deviceEventHistory-fieldLabelEndVisitType:End Visit Type`,
		clientName: $localize`:@@deviceEventHistory-filterLabelClientName:Client`,
		clientIDN: $localize`:@@deviceEventHistory-filterLabelClientIDN:Client IDN`,
	};

	getDeviceEventHistoryLoadingKey = ApiLoadingKeys.getDeviceEventHistory;
	getEventHistoryReportLoadingKey = ApiLoadingKeys.getEventHistoryReport;
	finishedLoadingInitData: boolean = false;

	disarmedAlarmText = disarmedAlarmText;
	armedAlarmText = armedAlarmText;
	alarmOverrideText = alarmOverrideText;

	constructor(
		private apiService: ApiService,
		private readonly systemCapabilityService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService,
		private readonly calendarDateService: CalendarDateService
	) {
		this.updateEventHistoryAccess();
		this.fetchCompanyType();
	}

	ngAfterViewInit(): void {
		if (!this.dataSet?.queryParams) return;

		this.filterSubscription = this.dataSet?.queryParams
			?.pipe(
				map((filters) =>
					this.calendarDateService.mapDateFiltersToCalendarDate(filters)
				),
				tap((filters) => (this.filters = filters)),
				mergeMap(async (filter) => {
					const result = await this.apiService.getDeviceEventHistory(
						this.deviceId,
						filter
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((history) => {
				this.dataSource.data = history;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	async generateEventHistoryReport(): Promise<void> {
		this.isDownloadReportDisabled = true;
		await this.apiService
			.getEventHistoryReport(this.deviceId, 'device', this.filters)
			.then((_) => {
				this.isDownloadReportDisabled = false;
			});
	}

	rowId(row: EventHistory): string {
		if (!row?.deviceId || !row?.utcStartDate || !row?.entityRelationshipId) {
			return 'unknown-id';
		}

		return `${row.deviceId}-${row.utcStartDate}-${row.entityRelationshipId}`;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['device'] || changes['userProfile']) {
			this.updateEventHistoryAccess();
		}
	}

	private updateEventHistoryAccess(): void {
		this.canGenerateEventHistoryReport =
			(this.systemCapabilityService.has(
				SystemCapabilityEnum.deviceManagement
			) &&
				this.systemCapabilityService.has(
					SystemCapabilityEnum.globalCompanyManagement
				)) ||
			(this.systemCapabilityService.has(SystemCapabilityEnum.runReports) &&
				this.isDeviceOwnedByCompany());
	}

	private isDeviceOwnedByCompany(): boolean {
		// For audit capability, user can only see event history if their company owns the device
		return (
			this.device?.activeDeviceAssignment?.company?.id ===
			this.userProfile?.activeRoleDetails?.company?.id
		);
	}

	// Fetch company information to get company type and can access the 'care' features :
	async fetchCompanyType(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		let activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.companyTypeFlag = companyData?.company?.companyType === 'care';
		}

		this.displayedColumns = this.companyTypeFlag
			? [
					'userName',
					'companyName',
					'clientName',
					'clientIDN',
					'locationName',
					'deviceLocation',
					'eventType',
					'eventStart',
					'eventEnd',
					'endVisitType',
				]
			: [
					'userName',
					'companyName',
					'locationName',
					'deviceLocation',
					'eventType',
					'eventStart',
					'eventEnd',
					'endVisitType',
				];
	}
}
